import { useCallback, useEffect, useRef, useState } from 'react';
import { useChatStore } from '../../store/chatStore';
import { useAuthStore } from '../../store/authStore';
import { useSSE, type SseStatus } from '../../hooks/useSSE';
import { listMyRooms, getMessages, markRoomRead, getRoomReadStates } from '../../api/chat';
import type {
  AiActionStatusEvent,
  ChatMessageEvent,
  ChatMessageDeltaEvent,
  ChatMessageCompleteEvent,
  ChatRoomDto,
} from '../../types/chat';
import { ChatList } from './ChatList';
import { ChatArea } from './ChatArea';
import './ChatPage.css';

/**
 * ChatPage (P2-16) — 시안 line 1063+1124 의 ChatList(280px) + ChatArea 2단 레이아웃.
 *
 * 다른 메뉴들은 단일 영역(NavBar 외 1열)이지만 chat 만 2열. AppLayout 의 `app-content`
 * 안에서 horizontal flex 로 결합한다.
 *
 * 데이터 흐름:
 *   1. mount → listMyRooms() 로 좌측 패널 채움
 *   2. user click → setActiveChat(roomId) → getMessages(roomId) catch-up
 *   3. useSSE 가 활성 room 만 구독 → store.appendMessage 로 실시간 push
 *   4. unmount / room 전환 시 SSE close (useSSE 내부 처리)
 *
 * P2-16 코어 골격 — AI 카드 상세 (P2-16d/e/f/g/h) 는 placeholder, P2-17 attachment / P2-18
 * E2E / P2-24 storybook 은 후속 사이클.
 */

export function ChatPage() {
  const activeChatId = useChatStore((s) => s.activeChatId);
  const appendMessages = useChatStore((s) => s.appendMessages);
  const appendMessage = useChatStore((s) => s.appendMessage);
  const markMessageDeleted = useChatStore((s) => s.markMessageDeleted);
  const getRoomMessages = useChatStore.getState; // store 자체 — effect 내부에서 latest 값 조회.

  const [rooms, setRooms] = useState<ChatRoomDto[]>([]);
  const [roomsError, setRoomsError] = useState<string | null>(null);
  const [roomsLoading, setRoomsLoading] = useState(false);
  const [sseStatus, setSseStatus] = useState<SseStatus>('idle');
  // catch-up 이미 시도한 room id 셋 — useEffect 가 messagesByRoom 의존성 없이도 재호출 회피.
  const catchUpFetchedRef = useRef<Set<string>>(new Set());

  // 1) 진입 시 본인의 room 목록 로드.
  useEffect(() => {
    let cancelled = false;
    setRoomsLoading(true);
    setRoomsError(null);
    listMyRooms({ limit: 50 })
      .then((res) => {
        if (cancelled) return;
        setRooms(res.rooms);
        // 미읽음 맵을 chatStore SSOT 로 반영 (NavBar 합계 배지 + ChatList 방별 배지 공유).
        const map: Record<string, number> = {};
        for (const r of res.rooms) map[r.id] = r.unreadCount ?? 0;
        useChatStore.getState().setRoomUnreads(map);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setRoomsError(err instanceof Error ? err.message : 'rooms_fetch_failed');
      })
      .finally(() => {
        if (!cancelled) setRoomsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // 2) 활성 room 변경 시 catch-up — review HIGH-1/2 봉합:
  //    - dependency 에서 messagesByRoom 제거 (SSE push 마다 재실행 회피).
  //    - 이미 fetch 한 room 은 ref set 으로 skip — 효과적으로 single-flight.
  //    - setMessages (전체 교체) → appendMessages (merge) 로 변경: SSE 가 catch-up 보다
  //      먼저 도착해도 중복 commitSeq dedup 으로 손실 없음.
  useEffect(() => {
    if (!activeChatId) return;
    if (catchUpFetchedRef.current.has(activeChatId)) return;
    const existing = getRoomMessages().messagesByRoom[activeChatId];
    if (existing && existing.length > 0) {
      catchUpFetchedRef.current.add(activeChatId);
      return;
    }
    catchUpFetchedRef.current.add(activeChatId);
    let cancelled = false;
    // 초기 로드: 오늘+어제(달력일) 시간창 우선, 창 안이 적으면 최소 INITIAL_MIN 보장, INITIAL_CAP 상한.
    // 응답 nextCursor 를 historyCursor 로 보관 → '이전 대화 불러오기' 버튼 노출/이전 페이지 로드.
    getMessages(activeChatId, {
      since: startOfYesterdayISO(),
      minCount: INITIAL_MIN,
      limit: INITIAL_CAP,
    })
      .then((res) => {
        if (cancelled) return;
        appendMessages(res.messages);
        useChatStore.getState().setHistoryCursor(activeChatId, res.nextCursor);
      })
      .catch(() => {
        // 실패 시 ref 유지 — 같은 room 재진입 자동 재시도 회피.
        // 사용자가 manual reconnect 하면 catchUpFetchedRef.current.delete(activeChatId) 로 reset (P2-16 후속).
      });
    return () => {
      cancelled = true;
    };
  }, [activeChatId, appendMessages, getRoomMessages]);

  // 2-b) 활성 room 열람 시 읽음 처리 — 낙관적으로 배지 즉시 0 (chatStore) + 서버 lastReadId 전진.
  //
  // chat-read-receipts (2026-07-29) — 읽음 상태 맵 조회를 **먼저** 하고 그 다음에 읽음 처리를 보낸다.
  // 순서가 뒤집히면 markRoomRead 가 내 lastReadId 를 최신으로 밀어버려, 그 뒤 조회한 myLastReadId
  // 가 "이미 다 읽음" 이 되고 "여기부터 읽지 않았습니다" 구분선이 통째로 사라진다. 두 요청을 나란히
  // 쏘면 어느 쪽이 서버에 먼저 닿을지 보장되지 않으므로 명시적으로 직렬화한다.
  //
  // 조회가 실패해도 markRoomRead 는 반드시 보낸다(finally) — 건너뛰면 미읽음 배지가 영영 안 지워진다.
  // 사용자 체감 지연은 없다: 배지는 위 clearRoomUnread 로 이미 낙관적으로 0 이 된다.
  //
  // 방을 떠날 때 anchor 를 폐기 → 다음 진입에서 그 시점 기준으로 다시 계산된다.
  useEffect(() => {
    if (!activeChatId) return;
    const roomId = activeChatId;
    let cancelled = false;
    useChatStore.getState().clearRoomUnread(roomId);
    void getRoomReadStates(roomId)
      .then((res) => {
        if (cancelled) return;
        useChatStore.getState().setRoomReadStates(roomId, res.states, res.myLastReadId);
      })
      .catch(() => { /* best-effort — 배지/구분선 미표시로 degrade */ })
      .finally(() => {
        void markRoomRead(roomId).catch(() => { /* 실패는 무시 — 다음 진입/리프레시 시 회복 */ });
      });
    return () => {
      cancelled = true;
      useChatStore.getState().clearUnreadAnchor(roomId);
    };
  }, [activeChatId]);

  // 3) SSE 구독 — activeChatId 가 falsy 면 enabled false 로 connect 회피.
  //    initialLastEventId 는 mount 시점 또는 room 전환 시점의 store 마지막 commitSeq.
  //    이후 SSE 가 push 한 메시지는 useSSE 내부 lastEventIdRef 가 추적.
  const initialLastSeq = useChatStore((s) => {
    if (!activeChatId) return undefined;
    const arr = s.messagesByRoom[activeChatId];
    return arr && arr.length > 0 ? arr[arr.length - 1]!.commitSeq : undefined;
  });
  const myId = useAuthStore((s) => s.user?.id?.toString() ?? '');
  const handleMessage = useCallback(
    (msg: ChatMessageEvent) => {
      appendMessage(msg);
      // chat-read-receipts — 발신자는 자기가 보낸 메시지까지는 당연히 읽은 상태다(서버도 발신 tx
      // 에서 발신자 lastReadId 를 이 메시지로 전진시킨다). 그 사실을 chat_read 이벤트를 기다리지
      // 않고 즉시 반영해, 그 사람이 안 읽은 것으로 잠깐 표시되는 깜빡임을 없앤다.
      if (msg.senderId && !msg.isAi) {
        useChatStore.getState().applyReadReceipt(msg.roomId, msg.senderId, msg.id);
      }
      // 활성 room 을 보는 중 타인 메시지 도착 → 즉시 읽음 유지 (배지 0 + 서버 lastReadId 전진).
      if (msg.senderId && msg.senderId !== myId) {
        useChatStore.getState().clearRoomUnread(msg.roomId);
        void markRoomRead(msg.roomId).catch(() => { /* best-effort */ });
      }
    },
    [appendMessage, myId],
  );

  // chat-read-receipts — 같은 방 멤버가 읽음 처리(sse.chat_read) → 내 메시지의 미읽음 인원 배지 감소.
  const applyReadReceipt = useChatStore((s) => s.applyReadReceipt);
  const handleChatRead = useCallback(
    (e: { roomId: string; userId: string; lastReadId: string }) => {
      applyReadReceipt(e.roomId, e.userId, e.lastReadId);
    },
    [applyReadReceipt],
  );

  // sse.ai_action_status push → chatStore.actionsByActionId overlay 갱신.
  // 같은 room 의 다른 탭/디바이스가 결재한 경우, 본 push 가 LeaveRequestCard 의 status 를 자동 sync.
  // (pattern-server-state-machine-with-409-current-state-sync 의 push 변형)
  const updateAction = useChatStore((s) => s.updateAction);
  const handleAiActionStatus = useCallback(
    (ev: AiActionStatusEvent) => {
      updateAction(ev.actionId, ev.status);
    },
    [updateAction],
  );

  // sse.ai_delta·sse.ai_complete (adr-20260430-2235-ai-gateway-streaming).
  // chatStore.streamingByRoom 에 chunk 누적 + complete 시 isStreaming=false. final sse.message 가
  // 별도 path 로 도달하면 messagesByRoom 에 정식 등록되고, 본 임시 메시지는 아래 handleMessage 가
  // removeStream 으로 정리.
  const appendChunk = useChatStore((s) => s.appendChunk);
  const finalizeStream = useChatStore((s) => s.finalizeStream);
  const removeStream = useChatStore((s) => s.removeStream);
  const evictStaleStreamsAll = useChatStore((s) => s.evictStaleStreamsAll);

  // R4 fix (followup-cycle-streaming-C-leak): anthropic timeout 등으로 ai.complete 미발화 시
  // streamingByRoom 의 isStreaming=true partial entry 가 영속 잔존하는 leak 안전망.
  // 1분 폴링 + 5분 stale 임계 → 최대 노출 시간 6분.
  // F-MED-2 fix (followup-D): 'all' sentinel 대신 dedicated evictStaleStreamsAll 사용.
  useEffect(() => {
    const intervalId = window.setInterval(() => {
      evictStaleStreamsAll(5 * 60 * 1000);
    }, 60 * 1000);
    return () => window.clearInterval(intervalId);
  }, [evictStaleStreamsAll]);

  const handleAiDelta = useCallback(
    (ev: ChatMessageDeltaEvent) => {
      appendChunk(ev.roomId, ev.streamMessageId, ev.delta);
    },
    [appendChunk],
  );
  const handleAiComplete = useCallback(
    (ev: ChatMessageCompleteEvent) => {
      finalizeStream(ev.roomId, ev.streamMessageId, ev.text);
    },
    [finalizeStream],
  );

  // orchestrator-svc 의 도구 사용 lifecycle — start 시 tool 이름 표시, done 시 clear (final 메시지 도착 시 appendMessage 가 한 번 더 clear).
  const setAiTool = useChatStore((s) => s.setAiTool);
  const handleAiToolUse = useCallback(
    (ev: { roomId: string; phase: 'start' | 'done'; tool: string }) => {
      setAiTool(ev.roomId, ev.phase === 'start' ? ev.tool : null);
    },
    [setAiTool],
  );

  useSSE({
    roomId: activeChatId ?? '',
    enabled: !!activeChatId,
    initialLastEventId: initialLastSeq,
    onMessage: handleMessage,
    onAiActionStatus: handleAiActionStatus,
    onAiDelta: handleAiDelta,
    onAiComplete: handleAiComplete,
    onAiToolUse: handleAiToolUse,
    onChatRead: handleChatRead,
    onMessageDeleted: (e) => markMessageDeleted(e.roomId, e.messageId),
    // 방 자동 삭제(나가기 후 사용자 1인+AI 잔류) — 활성-룸 스트림이 user 채널로 수신.
    // 어떤 방을 보고 있든 남은 멤버의 user 채널로 도달하므로 기존 나가기 정리 로직 재사용.
    onRoomDeleted: (e) => handleLeaveRoom(e.roomId),
    onStatusChange: setSseStatus,
  });

  // final sse.message 도착 시 임시 streaming 메시지 정리 (R1 fix).
  //
  // 1차 매칭: server 가 sse.message payload 에 set 한 streamMessageId 정확 키 매칭.
  // 2차 fallback: 옛 server (streamMessageId 미발화) 와의 호환 — content+isStreaming=false 비교.
  //   fallback 은 wire 호환만 — 새 server 와는 절대 도달하지 않음 (silent dead path 방지를
  //   위해 1차 매칭이 정확하면 fallback 스캔 자체를 skip).
  const messagesLen = useChatStore((s) =>
    activeChatId ? (s.messagesByRoom[activeChatId]?.length ?? 0) : 0,
  );
  // R7 fix: streaming entry 변경에도 cleanup effect 가 재발화 — sse.message-first race
  // (final sse.message 가 ai_delta·ai_complete 보다 먼저 도달) 봉합. 안정 정렬 키로 비교.
  const streamingKeySig = useChatStore((s) =>
    activeChatId
      ? Object.keys(s.streamingByRoom[activeChatId] ?? {}).sort().join('|')
      : '',
  );
  useEffect(() => {
    if (!activeChatId || messagesLen === 0) return;
    const state = useChatStore.getState();
    const roomStreams = state.streamingByRoom[activeChatId];
    if (!roomStreams) return;
    const messages = state.messagesByRoom[activeChatId];
    if (!messages || messages.length === 0) return;

    // 1차 — streamMessageId 가 set 된 모든 AI 메시지를 스캔하여 정확 매칭하는 sid 일괄 cleanup.
    // F-MED-1 fix (followup-cycle-streaming-D-leak-residual): 동시 다중 ai_error race 시 일부 sid
    // 가 안전망 5분 대기하던 문제 봉합. 1차 정확 키 매칭은 isStreaming 무관 정확성 → 안전.
    const matchedSids: string[] = [];
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i]!;
      if (!m.isAi) continue;
      const sid = m.streamMessageId;
      if (sid && roomStreams[sid]) {
        matchedSids.push(sid);
      }
    }
    if (matchedSids.length > 0) {
      for (const sid of matchedSids) {
        removeStream(activeChatId, sid);
      }
      return;
    }

    // 2차 fallback — 옛 server 호환. 가장 최근 AI 메시지 content 와 finalized streaming 매칭.
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i]!;
      if (!m.isAi) continue;
      if (m.streamMessageId) return; // 1차 분기에서 이미 처리되었어야 — silent skip
      for (const candidateSid of Object.keys(roomStreams)) {
        const s = roomStreams[candidateSid]!;
        if (!s.isStreaming && s.text === m.content) {
          removeStream(activeChatId, candidateSid);
          break;
        }
      }
      break;
    }
  }, [activeChatId, messagesLen, streamingKeySig, removeStream]);

  // 새 채팅 모달이 방을 생성/재사용 → rooms 목록 prepend(중복 방지) + 활성화.
  const handleRoomCreated = useCallback((room: ChatRoomDto) => {
    setRooms((prev) => (prev.some((r) => r.id === room.id) ? prev : [room, ...prev]));
    useChatStore.getState().setActiveChat(room.id);
  }, []);

  // 이전(과거) 메시지 로드 — historyCursor 가 있을 때만. 응답을 merge(appendMessages 가 정렬·dedup)
  // 하고 nextCursor 로 cursor 전진. column-reverse 라 prepend 시 스크롤 위치는 native scroll-anchoring
  // 이 visual bottom 기준으로 자동 보존 → 별도 스크롤 복원 불필요.
  const handleLoadOlder = useCallback(async (roomId: string) => {
    const store = useChatStore.getState();
    const cursor = store.historyCursorByRoom[roomId];
    if (!cursor || store.loadingOlderByRoom[roomId]) return;
    store.setLoadingOlder(roomId, true);
    try {
      const res = await getMessages(roomId, { cursor, limit: OLDER_PAGE });
      store.appendMessages(res.messages);
      store.setHistoryCursor(roomId, res.nextCursor);
    } catch {
      // best-effort — cursor 유지, 사용자가 버튼 재클릭으로 재시도.
    } finally {
      useChatStore.getState().setLoadingOlder(roomId, false);
    }
  }, []);

  // 방 나가기 — 로컬 목록에서 제거 + 활성 정리 + 메시지·미읽음 캐시 정리.
  const handleLeaveRoom = useCallback((roomId: string) => {
    setRooms((prev) => prev.filter((r) => r.id !== roomId));
    const store = useChatStore.getState();
    if (store.activeChatId === roomId) store.setActiveChat(null);
    // 메시지·입력·미읽음 잔여 cleanup (best-effort — 일부 setter 가 없으면 다음 fetch 가 회복)
    store.clearInput?.(roomId);
  }, []);

  return (
    <div className="chat-page">
      <ChatList
        rooms={rooms}
        loading={roomsLoading}
        error={roomsError}
        onCreateRoom={handleRoomCreated}
        onLeaveRoom={handleLeaveRoom}
      />
      <ChatArea
        room={rooms.find((r) => r.id === activeChatId) ?? null}
        sseStatus={sseStatus}
        onLoadOlder={handleLoadOlder}
      />
    </div>
  );
}

export default ChatPage;

// 초기 로드 정책 (요청: "현재 일자·시간 기준 하루 전까지만, 그 이전은 액션으로").
//   - INITIAL_MIN: 시간창이 비거나 적은(조용한) 방의 빈 화면 방지 최소 보장 개수.
//   - INITIAL_CAP: 폭주한 방의 초기 페이로드 상한 (나머지는 '이전 대화 불러오기').
//   - OLDER_PAGE: 이전 로드 1회당 페이지 크기.
const INITIAL_MIN = 30;
const INITIAL_CAP = 100;
const OLDER_PAGE = 50;

/** 어제 0시(로컬) ISO — "오늘+어제(달력일)" 시간창 하한. */
function startOfYesterdayISO(): string {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - 1);
  return d.toISOString();
}
